import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AccessCatalogNode,
  AuditActionType,
  AuditLogEntry,
  ManagedUserRow,
  RbacDisplayRole,
  RbacProfile,
  RbacProfileKey,
  RbacUserType,
  ScopeDisplay,
} from '../models/rbac.model';

export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IamUiCapabilities {
  canAccessModule: boolean;
  canAccessClientManagement: boolean;
  canCreateClient: boolean;
  canDeleteClient: boolean;
  canDeactivateClient: boolean;
  readOnly: boolean;
  canInvite: boolean;
  canViewActivities: boolean;
  canEditUser: boolean;
  canToggleUser: boolean;
  canDeleteUser: boolean;
  canChangeRole: boolean;
  inviteableUserTypes: RbacUserType[];
  inviteableRoles: RbacDisplayRole[];
}

export interface IamMeResponse {
  userId: number;
  email: string;
  fullName: string;
  rbacProfile: IamRbacProfileDto;
  capabilities: IamUiCapabilities;
  scopedClientIds: number[];
  /** Codes from iam_user_access (Product / Section / Screen). */
  accessCodes: string[];
}

export interface IamClientSummary {
  id: number;
  name: string;
}

export interface IamAccountSummary {
  id: number;
  clientId: number;
  name: string;
  accountType?: string | null;
}

export interface IamUserListQuery {
  page?: number;
  pageSize?: number;
  search?: string | null;
  userTypes?: RbacUserType[];
  roles?: RbacDisplayRole[];
  statuses?: ManagedUserRow['status'][];
  clientIds?: number[];
  accountNames?: string[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface IamInviteRequest {
  username: string;
  email: string;
  lastname: string;
  userType: RbacUserType;
  role: RbacDisplayRole;
  clientIds: number[];
  accountIds: number[];
  accountScopeExplicit?: boolean;
  accessCodes: string[];
  sendMail?: boolean;
}

export interface IamUpdateUserRequest {
  username: string;
  lastname: string;
  userType: RbacUserType;
  role: RbacDisplayRole;
  clientIds: number[];
  accountIds: number[];
  accountScopeExplicit?: boolean;
  accessCodes: string[];
}

export interface IamGroupAccess {
  status: 'NOT_APPLICABLE' | 'PENDING' | 'SYNCED' | 'FAILED' | 'REMOVED';
  lastError?: string | null;
  lastAttemptAt?: string | null;
}

export interface IamUserDetail {
  user: ManagedUserRow;
  username: string;
  lastname: string;
  clientIds: number[];
  accountIds: number[];
  accessCodes: string[];
  clientWideAccountAccess: boolean;
  groupAccess?: IamGroupAccess | null;
}

interface IamRbacProfileDto {
  key: RbacProfileKey;
  userType: RbacUserType;
  displayRole: RbacDisplayRole;
  label: string;
  clientScope: ScopeDisplay;
  accountScope: ScopeDisplay;
  clientPermissions: string[];
  accountPermissions: string[];
  userPermissions: string[];
  modules: { name: string; status: string; color: string }[];
}

interface IamUserRowDto {
  id: number;
  fullName: string;
  email: string;
  initials: string;
  userType: RbacUserType;
  displayRole: RbacDisplayRole;
  roleLabel: string;
  typeLabel: string;
  clientNames: string[];
  accountNames: string[];
  accessCodes: string[];
  status: ManagedUserRow['status'];
  statusLabel: string;
  lastActivity: string;
  rbacProfile: IamRbacProfileDto;
}

interface IamAccessCatalogNodeDto {
  code: string;
  label: string;
  level: AccessCatalogNode['level'];
  parentCode: string | null;
  allowedRoles: RbacProfileKey[];
  grantable?: boolean;
  children?: IamAccessCatalogNodeDto[];
}

interface IamActivityDto {
  id: number;
  timestamp: string;
  actorName: string;
  actorEmail: string;
  action: AuditActionType;
  actionLabel: string;
  targetUser: string;
  targetEmail: string;
  details: string;
  changes?: Array<{
    type?: string;
    label?: string;
    from?: string | null;
    to?: string | null;
    value?: string | null;
  }>;
}

export interface IamActivityListQuery {
  page?: number;
  pageSize?: number;
  search?: string | null;
  action?: AuditActionType | null;
}

@Injectable({ providedIn: 'root' })
export class IamUserService {
  private readonly baseUrl = `${environment.apiUrl}/v1/iam`;

  constructor(private http: HttpClient) {}

  getMe(): Observable<IamMeResponse> {
    return this.http.get<IamMeResponse>(`${this.baseUrl}/me`);
  }

  getAccessCatalog(): Observable<AccessCatalogNode[]> {
    return this.http
      .get<IamAccessCatalogNodeDto[]>(`${this.baseUrl}/access-catalog`)
      .pipe(map((nodes) => this.mapAccessCatalog(nodes)));
  }

  listClients(page = 0, pageSize = 100, search?: string | null): Observable<PagedResponse<IamClientSummary>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<PagedResponse<IamClientSummary>>(`${this.baseUrl}/clients`, { params });
  }

  listClientAccounts(clientId: number): Observable<IamAccountSummary[]> {
    return this.http.get<IamAccountSummary[]>(`${this.baseUrl}/clients/${clientId}/accounts`);
  }

  listUsers(query: IamUserListQuery = {}): Observable<PagedResponse<ManagedUserRow>> {
    let params = new HttpParams()
      .set('page', query.page ?? 0)
      .set('pageSize', query.pageSize ?? 100);
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    for (const type of query.userTypes ?? []) {
      params = params.append('userTypes', type);
    }
    for (const role of query.roles ?? []) {
      params = params.append('roles', role);
    }
    for (const status of query.statuses ?? []) {
      params = params.append('statuses', status);
    }
    for (const clientId of query.clientIds ?? []) {
      params = params.append('clientIds', clientId);
    }
    for (const accountName of query.accountNames ?? []) {
      params = params.append('accountNames', accountName);
    }
    if (query.sortBy) {
      params = params.set('sortBy', query.sortBy);
    }
    if (query.sortDir) {
      params = params.set('sortDir', query.sortDir);
    }
    return this.http.get<PagedResponse<IamUserRowDto>>(`${this.baseUrl}/users`, { params }).pipe(
      map((response) => ({
        ...response,
        items: (response.items ?? []).map((row) => this.mapUserRow(row)),
      }))
    );
  }

  inviteUser(request: IamInviteRequest): Observable<ManagedUserRow> {
    return this.http.post<IamUserDetail & { user: IamUserRowDto }>(`${this.baseUrl}/users`, request).pipe(
      map((response) => {
        if (!response?.user) {
          throw new Error('Invite response is missing user payload');
        }
        return this.mapUserRow(response.user);
      })
    );
  }

  getUserDetail(userId: number): Observable<IamUserDetail> {
    return this.http.get<IamUserDetail & { user: IamUserRowDto }>(`${this.baseUrl}/users/${userId}`).pipe(
      map((response) => ({
        username: response.username,
        lastname: response.lastname,
        clientIds: response.clientIds ?? [],
        accountIds: response.accountIds ?? [],
        accessCodes: response.accessCodes ?? [],
        clientWideAccountAccess: response.clientWideAccountAccess ?? false,
        groupAccess: response.groupAccess ?? null,
        user: this.mapUserRow(response.user),
      }))
    );
  }

  syncGroupAccess(userId: number): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.baseUrl}/cloud-identity/sync/users/${userId}`,
      {}
    );
  }

  updateUser(userId: number, request: IamUpdateUserRequest): Observable<ManagedUserRow> {
    return this.http.put<IamUserDetail & { user: IamUserRowDto }>(`${this.baseUrl}/users/${userId}`, request).pipe(
      map((response) => {
        if (!response?.user) {
          throw new Error('Update response is missing user payload');
        }
        return this.mapUserRow(response.user);
      })
    );
  }

  toggleUserStatus(userId: number, enabled: boolean): Observable<ManagedUserRow> {
    return this.http
      .patch<IamUserDetail & { user: IamUserRowDto }>(`${this.baseUrl}/users/${userId}/status`, { enabled })
      .pipe(map((response) => this.mapUserRow(response.user)));
  }

  deleteUser(userId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/users/${userId}`);
  }

  listActivities(query: IamActivityListQuery = {}): Observable<PagedResponse<AuditLogEntry>> {
    let params = new HttpParams()
      .set('page', query.page ?? 0)
      .set('pageSize', query.pageSize ?? 20);
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    if (query.action) {
      params = params.set('action', query.action);
    }
    return this.http.get<PagedResponse<IamActivityDto>>(`${this.baseUrl}/activities`, { params }).pipe(
      map((response) => ({
        ...response,
        items: (response.items ?? []).map((activity) => this.mapActivity(activity)),
      }))
    );
  }

  mapAccessCatalog(nodes: IamAccessCatalogNodeDto[]): AccessCatalogNode[] {
    return nodes.map((node) => ({
      code: node.code,
      label: node.label,
      level: node.level,
      parentCode: node.parentCode,
      allowedRoles: node.allowedRoles,
      grantable: node.grantable !== false,
      children: node.children?.length ? this.mapAccessCatalog(node.children) : undefined,
    }));
  }

  private mapUserRow(row: IamUserRowDto): ManagedUserRow {
    return {
      id: row.id,
      fullName: row.fullName,
      email: row.email,
      initials: row.initials,
      userType: row.userType,
      displayRole: row.displayRole,
      roleLabel: row.roleLabel,
      typeLabel: row.typeLabel,
      clientNames: row.clientNames ?? [],
      accountNames: row.accountNames ?? [],
      accessCodes: row.accessCodes ?? [],
      status: row.status,
      statusLabel: row.statusLabel,
      lastActivity: row.lastActivity,
      rbacProfile: this.mapRbacProfile(row.rbacProfile),
      rawUser: row,
    };
  }

  private mapRbacProfile(profile: IamRbacProfileDto | null | undefined): RbacProfile {
    return {
      key: profile?.key ?? 'INT_VIEWER',
      userType: profile?.userType ?? 'INTERNAL',
      displayRole: profile?.displayRole ?? 'VIEWER',
      label: profile?.label ?? '',
      clientScope: profile?.clientScope ?? { kind: 'assigned', label: '', items: [] },
      accountScope: profile?.accountScope ?? { kind: 'assigned', label: '', items: [] },
      clientPermissions: profile?.clientPermissions ?? [],
      accountPermissions: profile?.accountPermissions ?? [],
      userPermissions: profile?.userPermissions ?? [],
      modules: (profile?.modules ?? []).map((module) => ({
        name: module.name,
        status: module.status as RbacProfile['modules'][number]['status'],
        color: module.color as RbacProfile['modules'][number]['color'],
      })),
    };
  }

  private mapActivity(activity: IamActivityDto): AuditLogEntry {
    return {
      id: String(activity.id),
      timestamp: new Date(activity.timestamp),
      actorName: activity.actorName,
      actorEmail: activity.actorEmail,
      action: activity.action,
      actionLabel: activity.actionLabel,
      targetUser: activity.targetUser,
      targetEmail: activity.targetEmail,
      details: activity.details ?? '',
      changes: (activity.changes ?? []).map((change) => ({
        type: change.type === 'add' || change.type === 'remove' || change.type === 'profile' ? change.type : 'profile',
        label: change.label ?? '',
        from: change.from ?? null,
        to: change.to ?? null,
        value: change.value ?? null,
      })),
    };
  }
}
